/**
 * CollapsiblePageHeader — a drop-in replacement for `@primer/react`'s
 * `PageHeader` that auto-includes a sidebar trigger in its `LeadingAction`
 * slot on narrow viewports.
 *
 * Built to pair with `SidebarPageLayout`/`FilteredListPageLayout`: on the
 * narrow viewport where the sidebar rail is hidden behind a dialog, the
 * trigger needs a home in the page header. This wrapper provides exactly
 * that, with no introspection or `cloneElement` plumbing required.
 *
 * ## Opt out
 *
 * If your header needs a custom `LeadingAction` (and not the auto-included
 * sidebar trigger), drop back to the raw `<PageHeader>` from `@primer/react`
 * and render a `<SidebarPageLayout.SidebarTrigger />` (or
 * `<CollapsibleSidebar.Toggle />`) wherever it fits.
 *
 * ## Sub-components
 *
 * All `PageHeader.*` sub-components are re-exported as
 * `CollapsiblePageHeader.*` via direct reference assignment, so each one
 * preserves its primer `__SLOT__` symbol identity for free. The lone
 * exception is `ParentLink`, which is wrapped to navigate client-side (see
 * `CollapsibleParentLink` below); the wrapper forwards primer's `__SLOT__`
 * so it stays interchangeable with the raw `ParentLink` for slot detection.
 */
import {ExtendedLink} from '@github-ui/react-router'
import {PageHeader} from '@primer/react'
import {use, useMemo, type ComponentProps, type PropsWithChildren} from 'react'

import {SidebarTrigger} from './SidebarTrigger'
import {SidebarPageLayoutStateContext} from './state'

export type CollapsiblePageHeaderProps = ComponentProps<typeof PageHeader>

// Hide the sidebar trigger on viewports where the sidebar rail itself is
// visible — duplicating the affordance would be confusing.
const TRIGGER_HIDDEN_ON_DESKTOP = {narrow: false, regular: true, wide: true} as const

function CollapsiblePageHeader({children, ...props}: PropsWithChildren<CollapsiblePageHeaderProps>) {
  const {hasSidebar} = use(SidebarPageLayoutStateContext)

  return (
    <PageHeader {...props}>
      {hasSidebar ? (
        <PageHeader.LeadingAction hidden={TRIGGER_HIDDEN_ON_DESKTOP}>
          <SidebarTrigger />
        </PageHeader.LeadingAction>
      ) : null}
      {children}
    </PageHeader>
  )
}

CollapsiblePageHeader.displayName = 'CollapsiblePageHeader'

// Unlike `NavList.Item`/`Button`/Primer `Link` (which allow `as={Link} to={…}`),
// `PageHeader.ParentLink` only types an `href` API (not router props like `to`).
// Until that’s fixed upstream (primer/react#7977), bridge the forwarded `href`
// to `ExtendedLink`’s `to` so we keep Primer’s parent-link visuals while
// navigating client-side. `ExtendedLink` matchRoutes-checks the target and
// hard-navigates for routes the current router can’t match (legacy Rails, hashes).
// TODO(primer/react#7977): drop this adapter and use `as={ExtendedLink} to={…}`
// once `PageHeader.ParentLink` forwards rest props.
function makeParentLinkRouterAdapter(onClick: ComponentProps<typeof PageHeader.ParentLink>['onClick']) {
  return function ParentLinkRouterAdapter({
    href = '',
    ...props
  }: {href?: string} & Omit<ComponentProps<typeof ExtendedLink>, 'to'>) {
    return <ExtendedLink to={href} onClick={onClick} {...props} />
  }
}

/**
 * Drop-in replacement for `PageHeader.ParentLink` that navigates client-side.
 * Keeps the same `href` API as the raw component and must be rendered within
 * the app router (consistent with `CollapsibleNavList`).
 */
function CollapsibleParentLink({onClick, ...props}: Omit<ComponentProps<typeof PageHeader.ParentLink>, 'as'>) {
  // `onClick` is pulled out of the props handed to `PageHeader.ParentLink` (which
  // would drop it) and injected into the adapter instead. Memoize the adapter so
  // its component identity stays stable across renders and React doesn't remount
  // the link subtree.
  const adapter = useMemo(() => makeParentLinkRouterAdapter(onClick), [onClick])
  return <PageHeader.ParentLink {...props} as={adapter} />
}
CollapsibleParentLink.displayName = 'CollapsiblePageHeader.ParentLink'
// Forward Primer's slot symbol so the wrapper stays interchangeable with the raw
// `PageHeader.ParentLink` for slot detection. Primer assigns `__SLOT__` only to
// `ContextArea`/`LeadingAction` today (not `ParentLink`), so this is currently
// `undefined` — but it future-proofs the wrapper if Primer adds one later.
;(CollapsibleParentLink as {__SLOT__?: symbol}).__SLOT__ = (
  PageHeader.ParentLink as unknown as {__SLOT__?: symbol}
).__SLOT__

// Reference re-exports — each sub-component keeps its primer `__SLOT__`
// symbol identity, so primer's internal slot detection treats them
// identically to a raw `<PageHeader.X>`.
const CollapsiblePageHeaderCompound = Object.assign(CollapsiblePageHeader, {
  ContextArea: PageHeader.ContextArea,
  ParentLink: CollapsibleParentLink,
  ContextBar: PageHeader.ContextBar,
  TitleArea: PageHeader.TitleArea,
  ContextAreaActions: PageHeader.ContextAreaActions,
  LeadingAction: PageHeader.LeadingAction,
  Breadcrumbs: PageHeader.Breadcrumbs,
  LeadingVisual: PageHeader.LeadingVisual,
  Title: PageHeader.Title,
  TrailingVisual: PageHeader.TrailingVisual,
  TrailingAction: PageHeader.TrailingAction,
  Actions: PageHeader.Actions,
  Description: PageHeader.Description,
  Navigation: PageHeader.Navigation,
})

export {CollapsiblePageHeaderCompound as CollapsiblePageHeader}
